/**
 * @file seoSchema.js
 * @description Helpers to inject/remove JSON-LD structured data (schema.org) into the
 *   document head for SEO. CRA is client-rendered, so we inject imperatively — the same
 *   approach HomePage uses for <title>/meta/canonical. Googlebot renders JS, so a
 *   FAQPage block becomes eligible for rich results (the FAQ answer-box).
 * @module lib/seoSchema
 */
import { useEffect } from "react";

/** Inject (or update) a JSON-LD <script> with the given id. */
export function injectJsonLd(id, data) {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/** Remove a previously-injected JSON-LD <script>. */
export function removeJsonLd(id) {
  if (typeof document === "undefined") return;
  document.getElementById(id)?.remove();
}

/** Build a schema.org FAQPage object from {q, a} pairs (blank entries dropped). */
export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: (faqs || [])
      .filter((f) => f && f.q && f.a)
      .map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
  };
}

/**
 * Inject a FAQPage JSON-LD block for the life of the component, then clean it up.
 * @param {string} id   unique <script> id (one per page so multiple pages don't clash)
 * @param {Array<{q:string,a:string}>} faqs
 */
export function useFaqJsonLd(id, faqs) {
  const key = JSON.stringify((faqs || []).map((f) => [f.q, f.a]));
  useEffect(() => {
    const list = (faqs || []).filter((f) => f && f.q && f.a);
    if (!list.length) return undefined;
    injectJsonLd(id, buildFaqSchema(list));
    return () => removeJsonLd(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, key]);
}

const SITE_URL = "https://abacases.com";

/**
 * Official brand profiles. Single source of truth: feeds the Organization
 * `sameAs` below *and* the PublicFooter links, so the site and the schema can
 * never drift apart.
 *
 * Why this matters: "abacases" is one edit away from "abacuses", so Google
 * autocorrects the query unless it has enough evidence the string names a real
 * entity. `sameAs` is the primary corroboration signal — but it only carries
 * weight when the reference is reciprocal, hence the matching footer links.
 * Each profile must link back to abacases.com for the loop to close.
 *
 * Note: the Facebook handle is `abacaseshq`, not `abacases` — an unrelated
 * business already holds `aba.cases`, and Facebook treats periods as
 * insignificant, so every period-variant of the bare name collides with it.
 */
export const SOCIAL_LINKS = [
  { name: "Instagram", url: "https://www.instagram.com/aba_cases/" },
  { name: "Facebook", url: "https://www.facebook.com/abacaseshq" },
  { name: "TikTok", url: "https://www.tiktok.com/@abacases" },
];

/**
 * The ABAcases Facebook group where every live case is also posted (one case per
 * post). Single source of truth for the footer link and the Find Cases note.
 * Deliberately NOT in SOCIAL_LINKS: it is a private group, so it must not feed the
 * Organization `sameAs`, and the footer renders it as a text link, not a brand icon.
 */
export const FACEBOOK_GROUP = {
  name: "ABAcases Job Opportunities for ABA providers",
  url: "https://www.facebook.com/groups/abacases",
};

/** Organization + WebSite graph — identifies the brand to search engines. */
export function buildSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "ABAcases",
        alternateName: [
          "ABA Case Finder",
          "ABAcases Pay Tracker",
          "ABAcases for RBTs and BCBAs",
          "Free ABA Timesheet Tracker",
        ],
        url: `${SITE_URL}/`,
        logo: `${SITE_URL}/logo512.png`,
        description:
          "ABAcases is an ABA-specific marketplace where RBTs, BCBAs, BCaBAs, and ABA therapists find local cases by pay, schedule, location, and credential, with a free ABA timesheet and payment tracker built in.",
        sameAs: SOCIAL_LINKS.map((s) => s.url),
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: "ABAcases",
        description:
          "ABAcases is an ABA case finder for RBTs, BCBAs, BCaBAs, BTs, and ABA therapists to find local ABA cases by pay, schedule, location, and credential, plus a free ABA timesheet and payment tracker.",
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en",
      },
    ],
  };
}

/** Inject the site-wide Organization + WebSite schema once for the page's life. */
export function useSiteSchema(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    injectJsonLd("site-jsonld", buildSiteSchema());
    return () => removeJsonLd("site-jsonld");
  }, [enabled]);
}

/**
 * Build a BreadcrumbList from ordered { name, url } items. `url` may be a path
 * (resolved against the site origin) or absolute; the last item is the page.
 */
export function buildBreadcrumbSchema(items) {
  const abs = (u) => (/^https?:\/\//.test(u) ? u : `${SITE_URL}${u.startsWith("/") ? "" : "/"}${u}`);
  // Match the trailing-slash served form so breadcrumb item URLs agree with each
  // page's canonical (prerendered routes serve at /route/; "/" and "/es" don't).
  const withSlash = (u) => {
    if (!u) return u;
    const s = String(u);
    if (/[?#]/.test(s) || s.endsWith("/") || s.endsWith("/es")) return s;
    if (/\/[^/]*\.[^/]+$/.test(s)) return s;
    return s + "/";
  };
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: (items || [])
      .filter((it) => it && it.name)
      .map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        ...(it.url ? { item: withSlash(abs(it.url)) } : {}),
      })),
  };
}

/** Inject a BreadcrumbList JSON-LD block for the component's life. */
export function useBreadcrumbJsonLd(id, items) {
  const key = JSON.stringify((items || []).map((it) => [it.name, it.url]));
  useEffect(() => {
    const list = (items || []).filter((it) => it && it.name);
    if (!list.length) return undefined;
    injectJsonLd(id, buildBreadcrumbSchema(list));
    return () => removeJsonLd(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, key]);
}
